import * as React from 'react';
import { Box } from '@leagueplatform/genesis-core';
import { StatusReportDetailsCell } from './status-report-table-cells/status-report-details-cell';
import { StatusReportLinkableTextCell } from './status-report-table-cells/status-report-linkable-text-cell';
import {
  StatusReportStatusCell,
  type StatusReportTableRowStatusIconMap,
} from './status-report-table-cells/status-report-status-cell';
import type {
  StatusReportTableRowDetails,
  StatusReportTableRowLinkableText,
  StatusReportTableRowStatus,
  StatusReportTableRowStatusTextMap,
} from './status-report-table-row.types';

export type { StatusReportTableRowStatus, StatusReportTableRowStatusTextMap };
export type { StatusReportTableRowStatusIconMap } from './status-report-table-cells/status-report-status-cell';

export type StatusReportTableDefaultRendererKey =
  | 'status'
  | 'linkableText'
  | 'details';
export type StatusReportTableRendererKey =
  | StatusReportTableDefaultRendererKey
  | string;

type CellValueByType = {
  status: StatusReportTableRowStatus;
  linkableText: StatusReportTableRowLinkableText;
  details: StatusReportTableRowDetails;
};

export type StatusReportTableRendererProps<
  TItem,
  TType extends StatusReportTableDefaultRendererKey = StatusReportTableDefaultRendererKey,
> = {
  item: TItem;
  value: CellValueByType[TType];
  rowStatus?: StatusReportTableRowStatus;
  statusTextMap: StatusReportTableRowStatusTextMap;
  statusIconMap?: StatusReportTableRowStatusIconMap;
};

export type StatusReportTableRenderer<
  TItem,
  TType extends StatusReportTableDefaultRendererKey = StatusReportTableDefaultRendererKey,
> = React.FC<StatusReportTableRendererProps<TItem, TType>>;

type StatusReportTableRendererByType<TItem> = {
  [K in StatusReportTableDefaultRendererKey]: StatusReportTableRenderer<
    TItem,
    K
  >;
};

type AnyStatusReportTableRenderer<TItem> =
  StatusReportTableRendererByType<TItem>[StatusReportTableDefaultRendererKey];

export type StatusReportTableRendererRegistry<TItem> = Record<
  string,
  AnyStatusReportTableRenderer<TItem>
> &
  Partial<StatusReportTableRendererByType<TItem>>;

type StatusReportTableRowColumnBase<
  TType extends StatusReportTableDefaultRendererKey,
  TItem,
> = {
  id: string;
  type: TType;
  getValue: (item: TItem) => CellValueByType[TType];
  renderer?: StatusReportTableRendererKey;
};

export type StatusReportTableRowColumn<TItem> =
  | StatusReportTableRowColumnBase<'status', TItem>
  | StatusReportTableRowColumnBase<'linkableText', TItem>
  | StatusReportTableRowColumnBase<'details', TItem>;

const statusReportTableRowStatuses: readonly StatusReportTableRowStatus[] = [
  'success',
  'skipped',
  'loading',
  'error',
];

const isStatusReportTableRowStatus = (
  value: unknown,
): value is StatusReportTableRowStatus =>
  statusReportTableRowStatuses.includes(value as StatusReportTableRowStatus);

// Preserve status-aware default renderers if any consumer ever omit the status column
// but still provide item.status (e.g. details error text/link rendering).
const resolveItemRowStatus = (
  item: unknown,
): StatusReportTableRowStatus | undefined => {
  if (typeof item !== 'object' || item === null || !('status' in item)) {
    return undefined;
  }

  const { status } = item as { status: unknown };
  return isStatusReportTableRowStatus(status) ? status : undefined;
};

export const defaultStatusReportRendererRegistry: StatusReportTableRendererByType<any> =
  {
    status: ({ value, statusTextMap, statusIconMap }) => (
      <StatusReportStatusCell
        value={value}
        statusTextMap={statusTextMap}
        statusIconMap={statusIconMap}
      />
    ),
    linkableText: ({ value, rowStatus }) => (
      <StatusReportLinkableTextCell value={value} rowStatus={rowStatus} />
    ),
    details: ({ value, rowStatus }) => (
      <StatusReportDetailsCell value={value} rowStatus={rowStatus} />
    ),
  };

type StatusReportTableRowComponentProps<TItem> = {
  item: TItem;
  statusTextMap: StatusReportTableRowStatusTextMap;
  statusIconMap?: StatusReportTableRowStatusIconMap;
  columns: StatusReportTableRowColumn<TItem>[];
  rendererRegistry: StatusReportTableRendererRegistry<TItem>;
  /** 1-based row index for aria-rowindex in virtualized tables. */
  'aria-rowindex'?: number;
  /** Used by @tanstack/react-virtual to map measured elements to virtual items. */
  'data-index'?: number;
};

const StatusReportTableRowInner = <TItem,>(
  {
    item,
    statusTextMap,
    statusIconMap,
    columns,
    rendererRegistry,
    'aria-rowindex': ariaRowIndex,
    'data-index': dataIndex,
  }: StatusReportTableRowComponentProps<TItem>,
  ref: React.ForwardedRef<HTMLTableRowElement>,
) => {
  const statusColumn = columns.find(
    (column): column is StatusReportTableRowColumnBase<'status', TItem> =>
      column.type === 'status',
  );
  const rowStatus = statusColumn?.getValue(item) ?? resolveItemRowStatus(item);

  const renderTypedCell = <TType extends StatusReportTableDefaultRendererKey>(
    column: StatusReportTableRowColumnBase<TType, TItem>,
  ) => {
    const rendererKey = column.renderer ?? column.type;
    const value = column.getValue(item);
    const fallbackRenderer = defaultStatusReportRendererRegistry[
      column.type
    ] as StatusReportTableRenderer<TItem, TType>;
    const Renderer =
      (rendererRegistry[rendererKey] as
        | StatusReportTableRenderer<TItem, TType>
        | undefined) ?? fallbackRenderer;
    return (
      <Renderer
        key={column.id}
        item={item}
        value={value}
        rowStatus={rowStatus}
        statusTextMap={statusTextMap}
        statusIconMap={statusIconMap}
      />
    );
  };

  return (
    <Box as="tr" ref={ref} aria-rowindex={ariaRowIndex} data-index={dataIndex}>
      {columns.map((column) => renderTypedCell(column))}
    </Box>
  );
};

// React.forwardRef erases generic type parameters, so we re-assert the
// generic signature on the export to keep TItem inference for consumers.
export const StatusReportTableRow = React.forwardRef(
  StatusReportTableRowInner,
) as <TItem>(
  props: StatusReportTableRowComponentProps<TItem> &
    React.RefAttributes<HTMLTableRowElement>,
) => React.ReactElement | null;
